// src/components/GoogleMapsAutocomplete/GoogleMapsAutocomplete.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { apiMapsAutocomplete, apiMapsPlaceDetails } from "../../services/mapsConfigApi";

/**
 * Componente de autocomplete de endereço usando Google Places API
 * @param {Object} props
 * @param {string} props.value - Valor atual do input
 * @param {Function} props.onChange - Callback quando texto muda
 * @param {Function} props.onPlaceSelected - Callback quando lugar é selecionado (recebe place object)
 * @param {string} props.placeholder - Placeholder do input
 * @param {string} props.className - Classes CSS
 * @param {Object} props.inputStyle - Estilos inline do input
 * @param {boolean} props.disabled - Se deve estar desabilitado
 * @param {string} props.grupoId - Grupo para autenticar chamadas no backend
 */
export default function GoogleMapsAutocomplete({
  value,
  onChange,
  onPlaceSelected,
  placeholder = "Digite o endereço...",
  className = "",
  inputStyle = {},
  disabled = false,
  grupoId = "",
}) {
  const inputRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [predictions, setPredictions] = useState([]);
  const [error, setError] = useState("");

  const canSearch = useMemo(() => !!grupoId && !disabled, [grupoId, disabled]);

  useEffect(() => {
    if (!canSearch) {
      setLoading(false);
      return;
    }
    const query = String(value || "").trim();
    if (query.length < 3) {
      setPredictions([]);
      setIsOpen(false);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");

    let isActive = true;

    const timer = setTimeout(() => {
      apiMapsAutocomplete({ grupoId, input: query })
        .then((list) => {
          if (!isActive) return;
          setPredictions(list);
          setIsOpen(true);
        })
        .catch((err) => {
          if (!isActive) return;
          console.error("Erro ao buscar enderecos:", err);
          setError(err.message || "Erro ao buscar enderecos");
          setPredictions([]);
          setIsOpen(false);
        })
        .finally(() => {
          if (!isActive) return;
          setLoading(false);
        });
    }, 350);

    return () => {
      isActive = false;
      clearTimeout(timer);
    };
  }, [value, grupoId, canSearch]);

  const handleSelect = async (item) => {
    if (!item?.placeId) return;
    setLoading(true);
    setError("");
    try {
      const place = await apiMapsPlaceDetails({ grupoId, placeId: item.placeId });
      if (place?.address) {
        onChange && onChange(place.address);
      }
      if (onPlaceSelected && place) {
        onPlaceSelected({
          address: place.address,
          lat: place.lat,
          lng: place.lng,
          placeId: item.placeId,
          name: place.name,
        });
      }
      setIsOpen(false);
    } catch (err) {
      console.error("Erro ao buscar detalhes:", err);
      setError(err.message || "Erro ao buscar detalhes");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ position: "relative" }}>
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => onChange && onChange(e.target.value)}
        placeholder={placeholder}
        className={className}
        disabled={disabled}
        onFocus={() => predictions.length > 0 && setIsOpen(true)}
        onBlur={() => setTimeout(() => setIsOpen(false), 150)}
        style={{ width: "100%", ...inputStyle }}
      />

      {loading && (
        <div style={{ fontSize: 12, marginTop: 4, opacity: 0.7 }}>
          Buscando enderecos...
        </div>
      )}

      {error && (
        <div style={{ padding: "6px", background: "#fee", border: "1px solid #fcc", borderRadius: 4, marginTop: 6 }}>
          {error}
        </div>
      )}

      {isOpen && predictions.length > 0 && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            background: "#fff",
            border: "1px solid #ddd",
            borderRadius: 6,
            marginTop: 4,
            maxHeight: 220,
            overflowY: "auto",
            zIndex: 20,
            boxShadow: "0 6px 16px rgba(0,0,0,0.08)",
          }}
        >
          {predictions.map((item) => (
            <button
              key={item.placeId}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleSelect(item)}
              style={{
                display: "block",
                width: "100%",
                textAlign: "left",
                padding: "8px 10px",
                border: "none",
                background: "transparent",
                cursor: "pointer",
              }}
            >
              {item.description}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
